import { DataPoint } from "regression";
import { DataSource } from "../../../types";
import { DisplayUnits } from "./definitions";
import {
  convertDiameterFromCanonical,
  convertFlowFromCanonical,
  convertFlowToCanonical,
  convertHeadFromCanonical,
  convertHeadToCanonical,
  convertNpshrFromCanonical,
  convertPowerFromCanonical,
  convertViscosityFromCanonical,
  convertViscosityToCanonical,
  formatAxisLabel,
  getDisplayUnitLabel,
} from "./converters";

const mapDataPoints = (
  points: DataPoint[] = [],
  convertX: (value: number) => number,
  convertY: (value: number) => number
): DataPoint[] => points.map(([x, y]) => [convertX(x), convertY(y)] as DataPoint);

export const convertFlowHeadSeries = (
  points: DataPoint[] = [],
  displayUnits: DisplayUnits
): DataPoint[] =>
  mapDataPoints(
    points,
    (value) => convertFlowFromCanonical(value, displayUnits.flow) as number,
    (value) => convertHeadFromCanonical(value, displayUnits.head) as number
  );

export const convertFlowPowerSeries = (
  points: DataPoint[] = [],
  displayUnits: DisplayUnits
): DataPoint[] =>
  mapDataPoints(
    points,
    (value) => convertFlowFromCanonical(value, displayUnits.flow) as number,
    (value) => convertPowerFromCanonical(value, displayUnits.power) as number
  );

export const convertFlowEfficiencySeries = (
  points: DataPoint[] = [],
  displayUnits: DisplayUnits
): DataPoint[] =>
  mapDataPoints(
    points,
    (value) => convertFlowFromCanonical(value, displayUnits.flow) as number,
    (value) => value
  );

export const convertFlowNpshrSeries = (
  points: DataPoint[] = [],
  displayUnits: DisplayUnits
): DataPoint[] =>
  mapDataPoints(
    points,
    (value) => convertFlowFromCanonical(value, displayUnits.flow) as number,
    (value) => convertNpshrFromCanonical(value, displayUnits.npshr) as number
  );

export const buildPerformanceBaseDisplayData = (
  dataSource: DataSource,
  displayUnits: DisplayUnits
) => ({
  QH: convertFlowHeadSeries(dataSource.QH?.dummyPoints || [], displayUnits),
  QP: convertFlowPowerSeries(dataSource.QP || [], displayUnits),
  QEFF: convertFlowEfficiencySeries(dataSource.QEFF?.dummyPoints || [], displayUnits),
  QNPSH: dataSource.QNPSH
    ? convertFlowNpshrSeries(dataSource.QNPSH.dummyPoints || [], displayUnits)
    : [],
});

type FormNumberLike = number | string | null | undefined;

interface PerformanceFormLikeValues {
  quantity?: FormNumberLike;
  head?: FormNumberLike;
  viscosity?: FormNumberLike;
  sg?: FormNumberLike;
}

const normalizeFormNumber = (value: FormNumberLike): number | undefined => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : undefined;
};

const roundDisplayFormNumber = (value?: number | null) => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return undefined;
  }

  return Number(value.toFixed(2));
};

export const buildPerformanceFormDisplayValues = (
  values: PerformanceFormLikeValues,
  displayUnits: DisplayUnits
) => ({
  quantity:
    values.quantity === "" || values.quantity === null || values.quantity === undefined
      ? undefined
      : roundDisplayFormNumber(
          convertFlowFromCanonical(
            normalizeFormNumber(values.quantity),
            displayUnits.flow
          ) as number | undefined
        ),
  head:
    values.head === "" || values.head === null || values.head === undefined
      ? undefined
      : roundDisplayFormNumber(
          convertHeadFromCanonical(
            normalizeFormNumber(values.head),
            displayUnits.head
          ) as number | undefined
        ),
  viscosity:
    values.viscosity === "" || values.viscosity === null || values.viscosity === undefined
      ? undefined
      : roundDisplayFormNumber(
          convertViscosityFromCanonical(
            normalizeFormNumber(values.viscosity),
            displayUnits.viscosity,
            normalizeFormNumber(values.sg)
          )
        ),
});

export const buildPerformanceCanonicalFormValues = (
  values: PerformanceFormLikeValues,
  displayUnits: DisplayUnits
) => ({
  quantity:
    values.quantity === "" || values.quantity === null || values.quantity === undefined
      ? undefined
      : (convertFlowToCanonical(
          normalizeFormNumber(values.quantity),
          displayUnits.flow
        ) as number | undefined),
  head:
    values.head === "" || values.head === null || values.head === undefined
      ? undefined
      : (convertHeadToCanonical(
          normalizeFormNumber(values.head),
          displayUnits.head
        ) as number | undefined),
  viscosity:
    values.viscosity === "" || values.viscosity === null || values.viscosity === undefined
      ? undefined
      : convertViscosityToCanonical(
          normalizeFormNumber(values.viscosity),
          displayUnits.viscosity,
          normalizeFormNumber(values.sg)
        ),
});

interface PerformanceDisplayModelInput {
  displayUnits: DisplayUnits;
  quantity: number;
  head: number;
  power: number;
  npsh: number | null;
  diameter?: number;
  diameterUnit?: string;
  auxData: DataPoint[];
  qH2Data: DataPoint[];
  qEFF2Data: DataPoint[];
  qNPSH2Data?: DataPoint[];
  qP2Data: DataPoint[];
}

export const buildDisplayDiameter = (
  diameter: number | null | undefined,
  diameterUnit: string | null | undefined,
  displayUnits: DisplayUnits
) => {
  if (diameter === null || diameter === undefined) {
    return {
      value: diameter,
      unit: diameterUnit || displayUnits.diameter,
    };
  }

  if (diameterUnit === "%") {
    return {
      value: diameter,
      unit: "%",
    };
  }

  return {
    value: convertDiameterFromCanonical(diameter, displayUnits.diameter) as number,
    unit: getDisplayUnitLabel("diameter", displayUnits.diameter),
  };
};

export const buildPerformanceDisplayModel = ({
  displayUnits,
  quantity,
  head,
  power,
  npsh,
  diameter,
  diameterUnit,
  auxData,
  qH2Data,
  qEFF2Data,
  qNPSH2Data,
  qP2Data,
}: PerformanceDisplayModelInput) => {
  const diameterDisplay = buildDisplayDiameter(diameter, diameterUnit, displayUnits);

  return {
    quantity: convertFlowFromCanonical(quantity, displayUnits.flow) as number,
    head: convertHeadFromCanonical(head, displayUnits.head) as number,
    power: convertPowerFromCanonical(power, displayUnits.power) as number,
    npsh: convertNpshrFromCanonical(npsh, displayUnits.npshr) as number | null,
    diameter: diameterDisplay.value,
    diameterDisplayUnit: diameterDisplay.unit,
    auxData: convertFlowHeadSeries(auxData, displayUnits),
    qH2Data: convertFlowHeadSeries(qH2Data, displayUnits),
    qEFF2Data: convertFlowEfficiencySeries(qEFF2Data, displayUnits),
    qNPSH2Data: convertFlowNpshrSeries(qNPSH2Data || [], displayUnits),
    qP2Data: convertFlowPowerSeries(qP2Data, displayUnits),
    axisLabels: {
      flow: formatAxisLabel("flow", displayUnits.flow),
      head: formatAxisLabel("head", displayUnits.head),
      power: formatAxisLabel("power", displayUnits.power),
      npshr: formatAxisLabel("npshr", displayUnits.npshr),
      efficiency: formatAxisLabel("efficiency", displayUnits.efficiency),
    },
    units: {
      Q: getDisplayUnitLabel("flow", displayUnits.flow),
      H: getDisplayUnitLabel("head", displayUnits.head),
      P: getDisplayUnitLabel("power", displayUnits.power),
      NPSH: getDisplayUnitLabel("npshr", displayUnits.npshr),
      D: diameterDisplay.unit,
      V: getDisplayUnitLabel("viscosity", displayUnits.viscosity),
    },
  };
};
